import { createContext, useContext, useState, ReactNode } from 'react';
import { useEffect } from 'react';
import { useActiveSection } from '../hooks/useActiveSection';
import type { Category, WineCategory } from '../supabase/types';

type PageType = 'menu' | 'wine-list' | null;
type CategoryType = Category | WineCategory;

interface PageNavigationContextType {
  currentPageType: PageType;
  categories: CategoryType[];
  activeCategorySlug: string;
  isCardNavOpen: boolean;
  isOpeningHoursOpen: boolean;
  setIsCardNavOpen: (isOpen: boolean) => void;
  setIsOpeningHoursOpen: (isOpen: boolean) => void;
  setCurrentPageType: (pageType: PageType) => void;
  setCategories: (categories: CategoryType[]) => void;
  scrollToCategory: (slug: string) => void;
}

const PageNavigationContext = createContext<PageNavigationContextType | undefined>(undefined);

export function PageNavigationProvider({ children }: { children: ReactNode }) {
  const [currentPageType, setCurrentPageType] = useState<PageType>(null);
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [isCardNavOpen, setIsCardNavOpen] = useState(false);
  const [isOpeningHoursOpen, setIsOpeningHoursOpen] = useState(false);

  // Create category slugs for active section tracking
  const categorySlugs = categories.map(category =>
    category.name.toLowerCase().replace(/\s+/g, '-')
  );

  const { activeSection: activeCategorySlug, scrollToSection: scrollToCategory } = useActiveSection(
    categorySlugs,
    { headerHeight: 140 }
  );

  // Handle body scroll lock when CardNav is open
  useEffect(() => {
    if (isCardNavOpen || isOpeningHoursOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isCardNavOpen, isOpeningHoursOpen]);

  return (
    <PageNavigationContext.Provider
      value={{
        currentPageType,
        categories,
        activeCategorySlug,
        isCardNavOpen,
        isOpeningHoursOpen,
        setIsCardNavOpen,
        setIsOpeningHoursOpen,
        setCurrentPageType,
        setCategories,
        scrollToCategory,
      }}
    >
      {children}
    </PageNavigationContext.Provider>
  );
}

export function usePageNavigation() {
  const context = useContext(PageNavigationContext);
  if (context === undefined) {
    throw new Error('usePageNavigation must be used within a PageNavigationProvider');
  }
  return context;
}
